import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import ListaExclusion from '../components/ListaExclusion'
import './Configuracion.css'

const emptyForm = {
  telefono_dueno: '',
  metodos_pago: '',
  recargo_usdt: '',
  tipo_cambio_efectivo: '',
  politica_garantia: '',
  requiere_cita: false, 
  direccion_fisica: '',
  minutos_anticipacion_recordatorio: 30, 
  intervalo_citas_minutos: 30, // 🌟 NUEVO
  max_citas_por_horario: 1, // 🌟 NUEVO
  acepta_canje: false, 
  permuta_minima: '',
  preguntas_canje: '',
  ofrece_servicio_tecnico: false,
  reparaciones_ofrecidas: '',
  mensaje_cotizacion_tecnico: 'Aguardame un instante que te preparo la cotización sin cargo 🛠️'
}

export default function Configuracion({ toast, comercioId }) {
  const [form, setForm] = useState(emptyForm)
  const [loading, setLoading] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  const cargarConfiguracion = useCallback(async () => {
    if (!comercioId) return
    try {
      const { data, error } = await supabase
        .from('configuracion_comercios')
        .select('*')
        .eq('comercio_id', comercioId)
        .maybeSingle()

      if (error) throw error
      if (data) {
        setForm({
          telefono_dueno: data.telefono_dueno || '',
          metodos_pago: data.metodos_pago || '',
          recargo_usdt: data.recargo_usdt || '',
          tipo_cambio_efectivo: data.tipo_cambio_efectivo || '',
          politica_garantia: data.politica_garantia || '',
          requiere_cita: data.requiere_cita ?? false, 
          direccion_fisica: data.direccion_fisica || '',
          minutos_anticipacion_recordatorio: data.minutos_anticipacion_recordatorio ?? 30,
          intervalo_citas_minutos: data.intervalo_citas_minutos ?? 30, // 🌟 NUEVO
          max_citas_por_horario: data.max_citas_por_horario ?? 1, // 🌟 NUEVO
          acepta_canje: data.acepta_canje ?? false, 
          permuta_minima: data.permuta_minima || '',
          preguntas_canje: data.preguntas_canje || '',
          ofrece_servicio_tecnico: data.ofrece_servicio_tecnico ?? false,
          reparaciones_ofrecidas: data.reparaciones_ofrecidas || '',
          mensaje_cotizacion_tecnico: data.mensaje_cotizacion_tecnico || 'Aguardame un instante que te preparo la cotización sin cargo 🛠️'
        })
      }
    } catch (err) {
      console.error("Error cargando configuración:", err.message)
      toast('Error al cargar las configuraciones', 'error')
    } finally {
      setLoading(false)
      setIsDirty(false)
    }
  }, [comercioId, toast])

  useEffect(() => {
    let isMounted = true
    const timer = setTimeout(() => {
      if (isMounted) setLoading(false)
    }, 2500)

    if (comercioId) {
      cargarConfiguracion().then(() => {
        if (isMounted) setLoading(false)
      })
    } else {
      setLoading(false)
    }

    return () => {
      isMounted = false
      clearTimeout(timer)
    }
  }, [comercioId, cargarConfiguracion])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setForm(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }))
    setIsDirty(true)
  }

  const guardar = async (e) => {
    e.preventDefault()
    if (!comercioId) return
    setGuardando(true)
    try {
      const payload = { 
        comercio_id: comercioId, 
        ...form,
        minutos_anticipacion_recordatorio: parseInt(form.minutos_anticipacion_recordatorio, 10),
        intervalo_citas_minutos: parseInt(form.intervalo_citas_minutos, 10), // 🌟 NUEVO: Aseguramos que sea número
        max_citas_por_horario: parseInt(form.max_citas_por_horario, 10) // 🌟 NUEVO: Aseguramos que sea número
      }
      const { error } = await supabase
        .from('configuracion_comercios')
        .upsert(payload, { onConflict: 'comercio_id' })
      if (error) throw error
      toast('Configuración guardada correctamente', 'success')
      setIsDirty(false)
    } catch (err) {
      console.error("error al guardar:", err)
      toast('No se pudo guardar la configuración', 'error')
    } finally {
      setGuardando(false)
    }
  }

  if (loading) {
    return (
      <div className="config-loading">
        <svg className="config-spinner" viewBox="0 0 50 50">
          <circle cx="25" cy="25" r="20" fill="none" strokeWidth="4"></circle>
        </svg>
        <p>Cargando panel de configuración...</p>
      </div>
    )
  }

  return (
    <div className="config-container" style={{ paddingBottom: '100px' }}>
      
      <header className="config-header">
        <h1 className="config-title">Configuración del agente</h1>
        <p className="config-subtitle">
          Defina el comportamiento, las políticas comerciales y la modalidad de atención que utilizará su asistente virtual.
        </p>
      </header>

      <form onSubmit={guardar}>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', 
          gap: '1.5rem', 
          alignItems: 'start' 
        }}>
          
          {/* COLUMNA IZQUIERDA */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {/* Modalidad de Atención */}
            <section className="config-card">
              <h3 className="config-card-title">Modalidad de atención</h3>
              
              <div className="config-checkbox-group" style={{ marginBottom: '0.75rem' }}>
                <input 
                  type="checkbox" 
                  name="requiere_cita" 
                  id="requiere_cita"
                  className="config-checkbox"
                  checked={form.requiere_cita} 
                  onChange={handleChange}
                />
                <label htmlFor="requiere_cita" className="config-checkbox-label">
                  Requiere agendar cita previa para visitar el local
                </label>
              </div>

              <div className={`config-form-group ${form.requiere_cita ? 'mb-sm' : 'mb-0'}`}>
                <label htmlFor="direccion_fisica" className="config-label">Dirección física del local</label>
                <input 
                  type="text"
                  id="direccion_fisica"
                  name="direccion_fisica" 
                  className="config-input"
                  value={form.direccion_fisica} 
                  onChange={handleChange} 
                  placeholder="Ej: Avenida Colón 1234, Piso 2, Tandil" 
                />
                <p className="config-help-text" style={{ marginTop: '4px', marginBottom: 0 }}>
                  {form.requiere_cita 
                    ? 'La dirección se enviará únicamente al confirmar una cita.'
                    : 'La dirección estará pública para cualquier consulta.'}
                </p>
              </div>

              {form.requiere_cita && (
                <div className="config-internal-grid grid-compact mt-sm pt-sm animate-fade-in" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                  
                  <div className="config-form-group span-full mb-0">
                    <label htmlFor="minutos_anticipacion_recordatorio" className="config-label">Anticipación del recordatorio</label>
                    <select 
                      id="minutos_anticipacion_recordatorio"
                      name="minutos_anticipacion_recordatorio"
                      className="config-input"
                      value={form.minutos_anticipacion_recordatorio}
                      onChange={handleChange}
                      style={{ backgroundColor: 'var(--bg-card)', cursor: 'pointer' }}
                    >
                      <option value="15">15 minutos antes</option>
                      <option value="30">30 minutos antes</option>
                      <option value="60">1 hora antes</option>
                      <option value="1440">24 horas antes</option>
                    </select>
                  </div>

                  {/* 🌟 NUEVO: Controles de turnos integrados en la grilla */}
                  <div className="config-form-group mb-0">
                    <label htmlFor="intervalo_citas_minutos" className="config-label">Frecuencia de turnos</label>
                    <select 
                      id="intervalo_citas_minutos"
                      name="intervalo_citas_minutos"
                      className="config-input"
                      value={form.intervalo_citas_minutos}
                      onChange={handleChange}
                      style={{ backgroundColor: 'var(--bg-card)', cursor: 'pointer' }}
                    >
                      <option value="15">Cada 15 minutos</option>
                      <option value="30">Cada 30 minutos</option>
                      <option value="45">Cada 45 minutos</option>
                      <option value="60">Cada 1 hora</option>
                    </select>
                  </div>

                  <div className="config-form-group mb-0">
                    <label htmlFor="max_citas_por_horario" className="config-label">Límite por horario</label>
                    <input 
                      type="number"
                      id="max_citas_por_horario"
                      name="max_citas_por_horario"
                      className="config-input"
                      value={form.max_citas_por_horario}
                      onChange={handleChange}
                      min="1"
                      max="50"
                    />
                  </div>

                </div>
              )}
            </section>

            {/* Plan Canje */}
            <section className="config-card">
              <h3 className="config-card-title">Plan Canje y Equipos Usados</h3>
              <div className="config-checkbox-group" style={{ marginBottom: form.acepta_canje ? '0.75rem' : '0' }}>
                <input 
                  type="checkbox" 
                  name="acepta_canje" 
                  id="acepta_canje"
                  className="config-checkbox"
                  checked={form.acepta_canje} 
                  onChange={handleChange}
                />
                <label htmlFor="acepta_canje" className="config-checkbox-label">
                  Acepta equipos usados como parte de pago
                </label>
              </div>

              {form.acepta_canje && (
                <div className="config-internal-grid grid-compact animate-fade-in">
                  <div className="config-form-group span-full mb-0">
                    <label htmlFor="permuta_minima" className="config-label">Política de recepción</label>
                    <input 
                      type="text" 
                      id="permuta_minima"
                      name="permuta_minima" 
                      className="config-input"
                      value={form.permuta_minima} 
                      onChange={handleChange} 
                      placeholder="Ej: Modelos del iPhone 11 en adelante." 
                    />
                  </div>
                  <div className="config-form-group span-full mb-0">
                    <label htmlFor="preguntas_canje" className="config-label">Cuestionario obligatorio</label>
                    <textarea 
                      id="preguntas_canje"
                      name="preguntas_canje" 
                      className="config-input config-textarea"
                      style={{ minHeight: '60px' }}
                      value={form.preguntas_canje} 
                      onChange={handleChange} 
                      placeholder="Ej: ¿Porcentaje de batería? ¿Tiene detalles?" 
                    />
                  </div>
                </div>
              )}
            </section>

            {/* Servicio Técnico */}
            <section className="config-card">
              <h3 className="config-card-title">Servicio Técnico</h3>
              <div className="config-checkbox-group" style={{ marginBottom: form.ofrece_servicio_tecnico ? '0.75rem' : '0' }}>
                <input 
                  type="checkbox" 
                  name="ofrece_servicio_tecnico" 
                  id="ofrece_servicio_tecnico"
                  className="config-checkbox"
                  checked={form.ofrece_servicio_tecnico} 
                  onChange={handleChange}
                />
                <label htmlFor="ofrece_servicio_tecnico" className="config-checkbox-label">
                  Ofrece servicio técnico y reparaciones
                </label>
              </div>

              {form.ofrece_servicio_tecnico && (
                <div className="config-internal-grid grid-compact animate-fade-in">
                  <div className="config-form-group span-full mb-0">
                    <label htmlFor="reparaciones_ofrecidas" className="config-label">Tipos de reparaciones</label>
                    <textarea 
                      id="reparaciones_ofrecidas"
                      name="reparaciones_ofrecidas" 
                      className="config-input config-textarea"
                      style={{ minHeight: '60px' }}
                      value={form.reparaciones_ofrecidas} 
                      onChange={handleChange} 
                      placeholder="Ej: Cambios de módulo, batería, pin de carga." 
                    />
                  </div>
                  <div className="config-form-group span-full mb-0">
                    <label htmlFor="mensaje_cotizacion_tecnico" className="config-label">Mensaje de espera</label>
                    <input 
                      type="text" 
                      id="mensaje_cotizacion_tecnico"
                      name="mensaje_cotizacion_tecnico" 
                      className="config-input"
                      value={form.mensaje_cotizacion_tecnico} 
                      onChange={handleChange} 
                    />
                  </div>
                </div>
              )}
            </section>

          </div>

          {/* COLUMNA DERECHA */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {/* Derivación y Soporte */}
            <section className="config-card">
              <h3 className="config-card-title">Derivación y soporte</h3>
              <div className="config-form-group mb-0">
                <label htmlFor="telefono_dueno" className="config-label">WhatsApp del encargado</label>
                <input 
                  required
                  type="text"
                  id="telefono_dueno"
                  name="telefono_dueno"
                  className="config-input"
                  value={form.telefono_dueno}
                  onChange={handleChange}
                  placeholder="Ej: 5492494600176"
                />
                <p className="config-help-text">El bot derivará las alertas a este número ante casos complejos.</p>
              </div>
            </section>

            {/* Políticas Comerciales */}
            <section className="config-card">
              <h3 className="config-card-title">Políticas comerciales de venta</h3>
              <div className="config-internal-grid grid-compact">
                <div className="config-form-group mb-0">
                  <label htmlFor="metodos_pago" className="config-label">Métodos de pago</label>
                  <input 
                    type="text" 
                    id="metodos_pago" 
                    name="metodos_pago" 
                    className="config-input"
                    value={form.metodos_pago} 
                    onChange={handleChange} 
                    placeholder="Ej: Cash, transferencia, USDT" 
                  />
                </div>
                <div className="config-form-group mb-0">
                  <label htmlFor="recargo_usdt" className="config-label">Recargo USDT (%)</label>
                  <input 
                    type="text" 
                    id="recargo_usdt" 
                    name="recargo_usdt" 
                    className="config-input"
                    value={form.recargo_usdt} 
                    onChange={handleChange} 
                    placeholder="Ej: 5%" 
                  />
                </div>
                <div className="config-form-group span-full mb-0">
                  <label htmlFor="tipo_cambio_efectivo" className="config-label">Cotización ARS</label>
                  <input 
                    type="text" 
                    id="tipo_cambio_efectivo" 
                    name="tipo_cambio_efectivo" 
                    className="config-input"
                    value={form.tipo_cambio_efectivo} 
                    onChange={handleChange} 
                    placeholder="Ej: Dólar blue vendedor" 
                  />
                </div>
                <div className="config-form-group span-full mb-0">
                  <label htmlFor="politica_garantia" className="config-label">Garantía de equipos</label>
                  <input 
                    type="text" 
                    id="politica_garantia" 
                    name="politica_garantia" 
                    className="config-input"
                    value={form.politica_garantia} 
                    onChange={handleChange} 
                    placeholder="Ej: 3 meses por fallas de fábrica." 
                  />
                </div>
              </div>
            </section>

            <ListaExclusion toast={toast} comercioId={comercioId} />

          </div>

        </div>

        {/* Barra flotante fija de guardado */}
        <div style={{
          position: 'fixed',
          bottom: isDirty || guardando ? '24px' : '-100px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#0f172a',
          padding: '14px 28px',
          borderRadius: '50px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
          zIndex: 9999,
          transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
          border: '1px solid #334155',
          width: 'max-content'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ animation: 'pulse 2s infinite', height: '8px', width: '8px', backgroundColor: '#eab308', borderRadius: '50%' }} />
            <span style={{ color: '#cbd5e1', fontSize: '14px', fontWeight: '500' }}>Tenés cambios sin guardar</span>
          </div>
          
          <button 
            type="submit" 
            disabled={guardando} 
            className="btn-config-save"
            style={{ padding: '8px 18px', borderRadius: '30px', fontSize: '13px' }}
          >
            {guardando ? 'Guardando...' : 'Guardar configuración'}
          </button>
        </div>

      </form>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: .5; }
        }
      `}</style>

    </div>
  )
}